import React, { useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Curve, CurveModifier, Modifiers, SineModifierBlendMode } from 'curves';
import './ModifierEditor.scss';
import { ModifierCurveType } from '../ModifierCurveType';

type ModifierFieldType = 'number' | 'select' | 'boolean' | 'json' | 'csv';

export type ModifierFieldDefinition = {
  name: string;
  label: string;
  type: ModifierFieldType;
  defaultValue: any;
  options?: { label: string, value: any }[];
};

export type ModifierDefinition = {
  label: string;
  ctor: new (...args: any[]) => CurveModifier<any>;
  fields: ModifierFieldDefinition[];
};

interface ModifierEditorProps<T> {
  curve: React.RefObject<Curve<T>>;
  modifierType?: ModifierCurveType;
  onUpdate: () => void;
}

const sineBlendOptions = Object.keys(SineModifierBlendMode)
  .filter((key) => isNaN(Number(key)))
  .map((key) => ({ label: key, value: SineModifierBlendMode[key] }));

const rangeFields: ModifierFieldDefinition[] = [
  { name: 'rangeStart', label: 'Range Start', type: 'number', defaultValue: Number.MIN_VALUE },
  { name: 'rangeEnd', label: 'Range End', type: 'number', defaultValue: Number.MAX_VALUE },
];

function numberLikeDefinitions(modifiers: any, clampMin: any, clampMax: any, noiseAmplitude: any): ModifierDefinition[] {
  return [
    {
      label: 'Clamp',
      ctor: modifiers.Clamp,
      fields: [
        { name: 'min', label: 'Min', type: typeof clampMin === 'number' ? 'number' : 'json', defaultValue: clampMin },
        { name: 'max', label: 'Max', type: typeof clampMax === 'number' ? 'number' : 'json', defaultValue: clampMax },
        ...rangeFields,
      ],
    },
    {
      label: 'Noise',
      ctor: modifiers.Noise,
      fields: [
        { name: 'amplitude', label: 'Amplitude', type: typeof noiseAmplitude === 'number' ? 'number' : 'json', defaultValue: noiseAmplitude },
        { name: 'frequency', label: 'Frequency', type: 'number', defaultValue: 1 },
        { name: 'seed', label: 'Seed', type: 'number', defaultValue: 0 },
        ...rangeFields,
      ],
    },
    {
      label: 'Sine',
      ctor: modifiers.Sine,
      fields: [
        { name: 'blend', label: 'Blend', type: 'select', defaultValue: SineModifierBlendMode.Replace, options: sineBlendOptions },
        { name: 'amplitude', label: 'Amplitude', type: 'number', defaultValue: 0.1 },
        { name: 'wavelength', label: 'Wavelength', type: 'number', defaultValue: 1 },
        { name: 'phaseOffset', label: 'Phase Offset', type: 'number', defaultValue: 0 },
        ...rangeFields,
      ],
    },
    {
      label: 'Step',
      ctor: modifiers.Step,
      fields: [
        { name: 'stepLength', label: 'Step Length', type: 'number', defaultValue: 1 },
        ...rangeFields,
      ],
    },
  ];
}

export const modifierDefinitions: { [key in ModifierCurveType]: ModifierDefinition[] } = {
  Number: numberLikeDefinitions(Modifiers.Number, 0, 1, 0.1),
  Bezier: numberLikeDefinitions(Modifiers.Number, 0, 1, 0.1),
  RGB: numberLikeDefinitions(Modifiers.RGB, { r: 0, g: 0, b: 0 }, { r: 255, g: 255, b: 255 }, 25),
  HSV: numberLikeDefinitions(Modifiers.HSV, { h: 0, s: 0, v: 0 }, { h: 360, s: 100, v: 100 }, 10),
  List: numberLikeDefinitions(Modifiers.List, 0, 1, 0.1),
  Vector3: numberLikeDefinitions(Modifiers.Vector3, { x: 0, y: 0, z: 0 }, { x: 1, y: 1, z: 1 }, { x: 25, y: 25, z: 25 }),
  Boolean: [
    {
      label: 'Noise',
      ctor: Modifiers.Boolean.Noise,
      fields: [
        { name: 'amplitude', label: 'Amplitude', type: 'number', defaultValue: 0.1 },
        { name: 'frequency', label: 'Frequency', type: 'number', defaultValue: 1 },
        { name: 'seed', label: 'Seed', type: 'number', defaultValue: 0 },
        ...rangeFields,
      ],
    },
    {
      label: 'Sine',
      ctor: Modifiers.Boolean.Sine,
      fields: [
        { name: 'blend', label: 'Blend', type: 'select', defaultValue: SineModifierBlendMode.Replace, options: sineBlendOptions },
        { name: 'amplitude', label: 'Amplitude', type: 'number', defaultValue: 0.1 },
        { name: 'wavelength', label: 'Wavelength', type: 'number', defaultValue: 1 },
        { name: 'phaseOffset', label: 'Phase Offset', type: 'number', defaultValue: 0 },
        ...rangeFields,
      ],
    },
  ],
  String: [
    {
      label: 'Ranges',
      ctor: Modifiers.String.Ranges,
      fields: [
        { name: 'ranges', label: 'Ranges', type: 'json', defaultValue: [{ min: 48, max: 57 }, { min: 65, max: 90 }, { min: 97, max: 122 }] },
        { name: 'clamp', label: 'Clamp', type: 'boolean', defaultValue: true },
        { name: 'allowKeyframeValues', label: 'Allow Keyframe Values', type: 'boolean', defaultValue: true },
        ...rangeFields,
      ],
    },
    {
      label: 'Exclude',
      ctor: Modifiers.String.Exclude,
      fields: [
        { name: 'blacklist', label: 'Characters', type: 'csv', defaultValue: ['a', 'e', 'i', 'o', 'u'] },
        { name: 'useWhitelist', label: 'Use As Whitelist', type: 'boolean', defaultValue: false },
        { name: 'clamp', label: 'Clamp', type: 'boolean', defaultValue: true },
        ...rangeFields,
      ],
    },
  ],
  Object: [
    {
      label: 'Aggregate',
      ctor: Modifiers.Object.Aggregate,
      fields: [
        { name: 'elements', label: 'Elements', type: 'json', defaultValue: {} },
        ...rangeFields,
      ],
    },
  ],
};

export const anyDefinitions: ModifierDefinition[] = [
  {
    label: 'Cycles',
    ctor: Modifiers.Any.Cycles,
    fields: [
      { name: 'startTime', label: 'Start Time', type: 'number', defaultValue: 0 },
      { name: 'endTime', label: 'End Time', type: 'number', defaultValue: 0.5 },
      ...rangeFields,
    ],
  },
];

export function getDefinition(type: ModifierCurveType | undefined, modifier: CurveModifier<any>) {
  const definitions = [...(type ? modifierDefinitions[type] : []), ...anyDefinitions];
  return definitions.find((definition) => modifier instanceof definition.ctor);
}

export function getModifierArgs(modifier: CurveModifier<any>, definition: ModifierDefinition) {
  const storedArgs = (modifier as any).__curveDemoArgs;
  if (storedArgs !== undefined) return storedArgs;
  return definition.fields.map((field) => (modifier as any)[field.name] ?? field.defaultValue);
}

function createModifier(definition: ModifierDefinition, args: any[]) {
  const modifier = new definition.ctor(...args);
  (modifier as any).__curveDemoArgs = args;
  return modifier;
}

function displayValue(value: any, type: ModifierFieldType) {
  if (type === 'json') return JSON.stringify(value, null, 2);
  if (type === 'csv') return Array.isArray(value) ? value.join(', ') : value;
  return value;
}

function parseValue(value: string, field: ModifierFieldDefinition) {
  if (field.type === 'number') return Number(value);
  if (field.type === 'boolean') return value === 'true';
  if (field.type === 'json') return JSON.parse(value);
  if (field.type === 'csv') return value.split(',').map((entry) => entry.trim()).filter((entry) => entry.length > 0);
  return value;
}

function ModifierEditor<T>(props: ModifierEditorProps<T>): React.ReactElement {
  const [selectedModifier, setSelectedModifier] = useState(0);
  const [jsonErrors, setJsonErrors] = useState<{ [key: string]: string }>({});

  if (!props.curve.current) return <div>No curve found!</div>;
  if (props.modifierType === undefined) return <div>No modifier type configured for this demo.</div>;

  const availableModifiers = [...modifierDefinitions[props.modifierType], ...anyDefinitions];

  function updateModifier(index: number, definition: ModifierDefinition, fieldIndex: number, value: any) {
    if (!props.curve.current) return;
    const args = getModifierArgs(props.curve.current.modifiers[index], definition).slice();
    args[fieldIndex] = value;
    props.curve.current.modifiers[index] = createModifier(definition, args);
    props.curve.current.update();
    props.onUpdate();
  }

  return (
    <div className="modifier-editor">
      <Row className="modifier-add-row">
        <Col sm={8}>
          <Form.Control
            as="select"
            value={selectedModifier}
            onChange={(e) => setSelectedModifier(Number((e.target as HTMLInputElement).value))}
          >
            {availableModifiers.map((definition, index) => (
              <option value={index} key={definition.label}>{definition.label}</option>
            ))}
          </Form.Control>
        </Col>
        <Col sm={4}>
          <Button
            variant="link"
            className="modifier-add-icon fa-icon-button"
            onClick={() => {
              if (!props.curve.current) return;
              const definition = availableModifiers[selectedModifier];
              const args = definition.fields.map((field) => field.defaultValue);
              props.curve.current.addModifier(createModifier(definition, args) as CurveModifier<T>);
              props.onUpdate();
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
          </Button>
        </Col>
      </Row>
      {props.curve.current.modifiers.length === 0 ? (
        <div className="modifier-empty">No modifiers added.</div>
      ) : undefined}
      {props.curve.current.modifiers.map((modifier, modifierIndex) => {
        const definition = getDefinition(props.modifierType, modifier);
        if (definition === undefined) {
          return (
            <div className="modifier-card" key={`${modifier.constructor.name}-${modifierIndex}`}>
              <div className="modifier-header">
                <h5>{modifier.constructor.name}</h5>
              </div>
              <p>This modifier is not editable in the demo.</p>
            </div>
          );
        }

        const args = getModifierArgs(modifier, definition);

        return (
          <div className="modifier-card" key={`${definition.label}-${modifierIndex}`}>
            <div className="modifier-header">
              <h5>{definition.label}</h5>
              <Button
                variant="link"
                className="modifier-delete-icon fa-icon-button"
                onClick={() => {
                  if (!props.curve.current) return;
                  props.curve.current.modifiers.splice(modifierIndex, 1);
                  props.curve.current.update();
                  props.onUpdate();
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </div>
            {definition.fields.map((field, fieldIndex) => {
              const errorKey = `${modifierIndex}-${field.name}`;
              return (
                <Form.Group as={Row} controlId={errorKey} key={field.name}>
                  <Form.Label column sm={4}>{field.label}</Form.Label>
                  <Col sm={8}>
                    {field.type === 'select' ? (
                      <Form.Control
                        as="select"
                        defaultValue={args[fieldIndex]}
                        onChange={(e) => updateModifier(modifierIndex, definition, fieldIndex, Number((e.target as HTMLInputElement).value))}
                      >
                        {(field.options ?? []).map((option) => (
                          <option value={option.value} key={option.label}>{option.label}</option>
                        ))}
                      </Form.Control>
                    ) : field.type === 'boolean' ? (
                      <Form.Control
                        as="select"
                        defaultValue={args[fieldIndex].toString()}
                        onChange={(e) => updateModifier(modifierIndex, definition, fieldIndex, parseValue((e.target as HTMLInputElement).value, field))}
                      >
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </Form.Control>
                    ) : (
                      <Form.Control
                        as={field.type === 'json' ? 'textarea' : 'input'}
                        type={field.type === 'number' ? 'number' : 'text'}
                        defaultValue={displayValue(args[fieldIndex], field.type)}
                        onChange={(e) => {
                          try {
                            const nextValue = parseValue((e.target as HTMLInputElement).value, field);
                            if (field.type === 'number' && isNaN(nextValue)) return;
                            const nextErrors = { ...jsonErrors };
                            delete nextErrors[errorKey];
                            setJsonErrors(nextErrors);
                            updateModifier(modifierIndex, definition, fieldIndex, nextValue);
                          }
                          catch (error) {
                            setJsonErrors({ ...jsonErrors, [errorKey]: 'Invalid JSON' });
                          }
                        }}
                      />
                    )}
                    {jsonErrors[errorKey] ? <Form.Text className="text-danger">{jsonErrors[errorKey]}</Form.Text> : undefined}
                  </Col>
                </Form.Group>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export default ModifierEditor;
